<template>
  <v-dialog :model-value="modelValue" max-width="560" persistent @update:model-value="$emit('update:modelValue', $event)">
    <v-card rounded="lg" class="pcd-card">
      <!-- Header -->
      <div class="pcd-header">
        <div class="d-flex align-center ga-3">
          <div class="pcd-header__icon">
            <v-icon size="18">fa-solid fa-cart-shopping</v-icon>
          </div>
          <div>
            <h2 class="pcd-header__title">Purchase Credits</h2>
            <p class="pcd-header__subtitle">Select a package to add credits to your account</p>
          </div>
        </div>
        <v-btn icon variant="text" size="small" @click="close">
          <v-icon size="16">fa-solid fa-xmark</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <!-- Form Content -->
      <div v-if="!processing" class="pcd-body">
        <!-- Currency -->
        <div class="pcd-section">
          <span class="pcd-section__label">Currency</span>
          <div class="pcd-currency-row">
            <v-btn
              v-for="c in currencies"
              :key="c"
              :variant="selectedCurrency === c ? 'flat' : 'outlined'"
              :color="selectedCurrency === c ? 'primary' : undefined"
              size="small"
              rounded="lg"
              class="pcd-currency-row__btn"
              @click="selectedCurrency = c"
            >
              {{ currencySymbols[c] }} {{ c }}
            </v-btn>
          </div>
        </div>

        <!-- Export Credits -->
        <div v-if="currentPackages" class="pcd-section">
          <div class="pcd-section__header">
            <span class="pcd-section__icon pcd-section__icon--export">
              <v-icon size="12">fa-solid fa-download</v-icon>
            </span>
            <span class="pcd-section__label">Export Credits</span>
          </div>
          <div class="pcd-packages">
            <button
              v-for="(pkg, index) in currentPackages.export"
              :key="index"
              class="pcd-pkg"
              :class="{ 'pcd-pkg--selected': exportSelection === index + 1 }"
              @click="exportSelection = exportSelection === index + 1 ? undefined : index + 1"
            >
              <span class="pcd-pkg__amount">{{ pkg.amount.toLocaleString() }}</span>
              <span class="pcd-pkg__label">credits</span>
              <span class="pcd-pkg__price">{{ formatPrice(pkg.price) }}</span>
            </button>
          </div>
        </div>

        <!-- Mobile Credits -->
        <div v-if="currentPackages" class="pcd-section">
          <div class="pcd-section__header">
            <span class="pcd-section__icon pcd-section__icon--mobile">
              <v-icon size="12">fa-solid fa-mobile</v-icon>
            </span>
            <span class="pcd-section__label">Mobile Credits</span>
            <v-tooltip location="top" max-width="280">
              <template #activator="{ props: tp }">
                <v-icon v-bind="tp" size="14" color="primary" class="ml-1" style="cursor: help">fa-solid fa-circle-info</v-icon>
              </template>
              Adds mobile phone number data when exporting a contact. Requires 1 Export Credit to be used. Cannot be used on its own.
            </v-tooltip>
          </div>
          <div class="pcd-packages">
            <button
              v-for="(pkg, index) in currentPackages.mobile"
              :key="index"
              class="pcd-pkg"
              :class="{ 'pcd-pkg--selected': mobileSelection === index + 1 }"
              @click="mobileSelection = mobileSelection === index + 1 ? undefined : index + 1"
            >
              <span class="pcd-pkg__amount">{{ pkg.amount.toLocaleString() }}</span>
              <span class="pcd-pkg__label">credits</span>
              <span class="pcd-pkg__price">{{ formatPrice(pkg.price) }}</span>
            </button>
          </div>
        </div>

        <!-- Total -->
        <div class="pcd-total">
          <span class="pcd-total__label">Total</span>
          <span class="pcd-total__value" :class="{ 'pcd-total__value--active': totalPrice > 0 }">
            {{ formatPrice(totalPrice) }}
          </span>
        </div>
      </div>

      <!-- Processing State -->
      <div v-else class="pcd-processing">
        <v-progress-circular indeterminate color="primary" size="52" width="4" class="mb-5" />
        <div class="pcd-processing__title">Processing your payment...</div>
        <div class="pcd-processing__text">Complete the checkout in the new window</div>
      </div>

      <!-- Actions -->
      <div class="pcd-actions">
        <v-btn
          variant="outlined"
          rounded="lg"
          class="pcd-actions__btn"
          @click="processing ? cancelPurchase() : close()"
        >
          Cancel
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="!processing"
          color="primary"
          variant="flat"
          rounded="lg"
          prepend-icon="fa-solid fa-lock"
          :disabled="!hasSelection"
          class="pcd-actions__btn"
          @click="handlePurchase"
        >
          Proceed to payment
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PurchaseCreditCommand, CurrencyPricing } from '../types/user-credits.types'

const props = defineProps<{
  modelValue: boolean
  pricePackages: Record<string, CurrencyPricing> | null
  // Owned by the host: true while a checkout is in flight. Drives the "Processing..." view so the
  // host can clear it (on completion, cancel, or a closed Stripe tab) and return us to selection.
  processing?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  purchase: [command: PurchaseCreditCommand]
  cancel: []
}>()

const currencies = ['USD', 'EUR', 'GBP']
const currencySymbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3' }
const selectedCurrency = ref('EUR')
const exportSelection = ref<number | undefined>(undefined)
const mobileSelection = ref<number | undefined>(undefined)

const currentPackages = computed(() => {
  return props.pricePackages?.[selectedCurrency.value] ?? null
})

const hasSelection = computed(() => {
  return (exportSelection.value !== undefined) || (mobileSelection.value !== undefined)
})

const totalPrice = computed(() => {
  let total = 0
  if (currentPackages.value && exportSelection.value !== undefined) {
    const pkg = currentPackages.value.export[exportSelection.value - 1]
    if (pkg) total += pkg.price
  }
  if (currentPackages.value && mobileSelection.value !== undefined) {
    const pkg = currentPackages.value.mobile[mobileSelection.value - 1]
    if (pkg) total += pkg.price
  }
  return total
})

// Package selections are tier indices (1-based) shared across every currency — only the price
// differs. Changing the currency must therefore KEEP the chosen packages and just re-price them
// (Vue 2 parity); the old `watch` that cleared both selections on currency change was the bug.

function formatPrice(price: number): string {
  const symbol = currencySymbols[selectedCurrency.value] || ''
  return `${symbol}${price.toFixed(2)}`
}

function handlePurchase(): void {
  // The host flips `processing` on (it owns the checkout lifecycle); no local state to set here.
  emit('purchase', {
    exportCreditOption: exportSelection.value ?? 0,
    mobileCreditOption: mobileSelection.value ?? 0,
    currencyCode: selectedCurrency.value,
  })
}

// Cancel while processing: ask the host to stop polling. It clears `processing`, returning us to the
// selection view (the dialog stays open so the user can retry).
function cancelPurchase(): void {
  emit('cancel')
}

function close(): void {
  exportSelection.value = undefined
  mobileSelection.value = undefined
  // Closing via the X while a payment is in flight must also stop the host's polling.
  if (props.processing) emit('cancel')
  emit('update:modelValue', false)
}
</script>

<style scoped lang="scss">
/* ── Header ── */
.pcd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(#7c4dff, 0.1);
    color: #7c4dff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.85);
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.45);
    margin-top: 1px;
  }
}

/* ── Body ── */
.pcd-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Section ── */
.pcd-section {
  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--export {
      background: rgba(#7c4dff, 0.1);
      color: #7c4dff;
    }

    &--mobile {
      background: rgba(#46c8de, 0.1);
      color: #46c8de;
    }
  }

  &__label {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.55);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}

/* ── Currency Row ── */
.pcd-currency-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;

  &__btn {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.01em;
    min-width: 80px;
  }
}

/* ── Package Cards ── */
.pcd-packages {
  display: flex;
  gap: 10px;
}

.pcd-pkg {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 14px 12px;
  border-radius: 10px;
  border: 1.5px solid rgba(var(--v-border-color), 0.12);
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.25);
    box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.08);
    transform: translateY(-1px);
  }

  &--selected {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.04);
    box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));

    &:hover {
      transform: none;
    }

    .pcd-pkg__amount {
      color: rgb(var(--v-theme-primary));
    }

    .pcd-pkg__price {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__amount {
    font-size: 1.125rem;
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.85);
    letter-spacing: -0.01em;
    line-height: 1.2;
  }

  &__label {
    font-size: 0.625rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.35);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__price {
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-top: 4px;
  }
}

/* ── Total ── */
.pcd-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.025);
  border: 1px solid rgba(var(--v-border-color), 0.06);

  &__label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.5);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__value {
    font-size: 1.25rem;
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.3);
    letter-spacing: -0.01em;
    transition: color 0.2s ease;

    &--active {
      color: rgba(var(--v-theme-on-surface), 0.85);
    }
  }
}

/* ── Processing ── */
.pcd-processing {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;

  &__title {
    font-size: 1rem;
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.75);
    margin-bottom: 4px;
  }

  &__text {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
  }
}

/* ── Actions ── */
.pcd-actions {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid rgba(var(--v-border-color), 0.1);

  &__btn {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.01em;
  }
}
</style>
